import React, { useEffect, useRef } from 'react';
import type { SceneLayout } from './layout';

export interface HardwareVisualProps {
  onReady?: () => void;
  layout?: SceneLayout;
}

const IMAGE = `${process.env.PUBLIC_URL}/assets/hardware/hero-hardware.jpg`;
const DUST = Array.from({ length: 14 }, (_, i) => i);

// 2.5D hero visual for the /site2 variant: the rendered hardware set sits in
// a darkened pocket of the smoke, lighten-blended so its own dark background
// dissolves, with a cyan under-glow, drifting dust, a slow float and a
// pointer-driven tilt.
const HardwareVisual: React.FC<HardwareVisualProps> = ({ onReady, layout = 'split' }) => {
  const stageRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return undefined;
    const reduceMotion = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)').matches : false;
    if (reduceMotion) return undefined;

    const target = { x: 0, y: 0 };
    const current = { x: 0, y: 0 };
    let raf = 0;
    const onMove = (e: PointerEvent) => {
      target.x = (e.clientX / window.innerWidth) * 2 - 1;
      target.y = (e.clientY / window.innerHeight) * 2 - 1;
    };
    const tick = () => {
      raf = requestAnimationFrame(tick);
      current.x += (target.x - current.x) * 0.05;
      current.y += (target.y - current.y) * 0.05;
      stage.style.transform = `perspective(1400px) rotateY(${(current.x * 5).toFixed(2)}deg) rotateX(${(-current.y * 3.5).toFixed(2)}deg) translate3d(${(current.x * 16).toFixed(1)}px, ${(current.y * 9).toFixed(1)}px, 0)`;
    };
    window.addEventListener('pointermove', onMove, { passive: true });
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('pointermove', onMove);
    };
  }, []);

  return (
    <div className={`lp-hw lp-hw--${layout}`} aria-hidden="true">
      <div ref={stageRef} className="lp-hw-stage">
        <div className="lp-hw-float">
          <div className="lp-hw-pocket" />
          <div className="lp-hw-glow" />
          <img src={IMAGE} alt="" className="lp-hw-img" onLoad={() => onReady && onReady()} draggable={false} />
          <div className="lp-hw-dust">
            {DUST.map((i) => (
              <span key={i} style={{ left: `${8 + ((i * 37) % 84)}%`, top: `${10 + ((i * 53) % 80)}%`, animationDelay: `${(i * 0.7) % 6}s`, animationDuration: `${7 + (i % 5)}s` }} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HardwareVisual;
